import {
  forwardRef,
  lazy,
  Suspense,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  emptyProductPresentation,
  isProductImageUrl,
  parseProductPresentation,
  type ProductIntroBlock,
  type ProductPresentation,
} from '@travel/contracts';
import { ApiError, type AdminApiClient, type JsonRecord } from './api-client';
import { ConfirmDialog } from './admin-feedback';
import { ProductThumbnail } from './product-thumbnail';
import './product-presentation-editor.css';

const ProductRichEditor = lazy(() =>
  import('./product-rich-editor').then((module) => ({ default: module.ProductRichEditor })),
);

const record = (value: unknown): JsonRecord =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : {};
type StopDecoration = { name: string; tags: string[]; images: { url: string; caption: string }[] };
function readStops(value: unknown): StopDecoration[] {
  const stops = record(value).stops;
  return Array.isArray(stops)
    ? (stops.filter((v) => typeof v?.name === 'string') as StopDecoration[])
    : [];
}

type PresentationDraft = {
  presentation: ProductPresentation;
  decorations: StopDecoration[];
  gallery: string[];
};
function readDraft(binding: JsonRecord): PresentationDraft {
  const data = record(binding.detailContentOverride);
  let presentation = emptyProductPresentation();
  try {
    if (data.presentation) presentation = parseProductPresentation(data.presentation);
  } catch {
    /* Existing malformed content remains available in the original override until saved. */
  }
  return {
    presentation,
    decorations: readStops(data.itinerary),
    gallery: Array.isArray(data.galleryImageUrls)
      ? data.galleryImageUrls.filter((value): value is string => typeof value === 'string')
      : [],
  };
}

export type ProductPresentationEditorHandle = {
  validate: () => boolean;
  validateForPublish: () => boolean;
  save: () => Promise<boolean>;
  getSaveError: () => string;
};
export type ProductPresentationEditorState = { dirty: boolean; busy: boolean; ready: boolean };
export type ProductGallerySelectionState = {
  selectedUrls: string[];
  coverUrl: string;
  isDefault: boolean;
  disabled: boolean;
  onToggle: (url: string) => void;
  onReset: () => void;
};

type ProductPresentationEditorProps = {
  api: AdminApiClient;
  appId: string;
  product: JsonRecord;
  binding: JsonRecord;
  loading: boolean;
  loadError?: string | undefined;
  onDone: () => void;
  onDirtyChange: (dirty: boolean) => void;
  integrated?: boolean;
  galleryStart?: ReactNode;
  renderGallery?: (state: ProductGallerySelectionState) => ReactNode;
  afterGallery?: ReactNode;
  beforeSupplementary?: ReactNode;
  externalBusy?: boolean;
  onStateChange?: (state: ProductPresentationEditorState) => void;
};

export const ProductPresentationEditor = forwardRef<
  ProductPresentationEditorHandle,
  ProductPresentationEditorProps
>(function ProductPresentationEditor(
  {
    api,
    appId,
    product,
    binding,
    loading,
    loadError = '',
    onDone,
    onDirtyChange,
    integrated = false,
    galleryStart,
    renderGallery,
    afterGallery,
    beforeSupplementary,
    externalBusy = false,
    onStateChange,
  },
  ref,
) {
  const productId = String(product.id);
  const context = useMemo(() => ({ api, appId, productId }), [api, appId, productId]);
  const contextRef = useRef(context);
  contextRef.current = context;
  const mounted = useRef(false);
  const requestSequence = useRef(0);
  const initializedContext = useRef<typeof context | null>(null);
  const sourceBinding = useRef<JsonRecord>({});
  const observedBinding = useRef('');
  const pendingRemoteBinding = useRef<JsonRecord | null>(null);
  const [presentation, setPresentation] = useState<ProductPresentation>(emptyProductPresentation);
  const [decorations, setDecorations] = useState<StopDecoration[]>([]);
  const [gallery, setGallery] = useState<string[]>([]);
  const [baseline, setBaseline] = useState('');
  const [busy, setBusy] = useState(false),
    [message, setMessage] = useState(''),
    [failed, setFailed] = useState(false);
  const saveError = useRef('');
  const [remoteChanged, setRemoteChanged] = useState(false);
  const [awaitingToken, setAwaitingToken] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [confirmReload, setConfirmReload] = useState(false);
  const running = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestSequence.current += 1;
    };
  }, []);
  const draft = { presentation, decorations, gallery };
  const currentDraft = JSON.stringify(draft);
  const dirty = Boolean(baseline) && baseline !== currentDraft;
  const ready =
    !loading &&
    !loadError &&
    !awaitingToken &&
    Boolean(baseline) &&
    initializedContext.current === context;
  const stateChangeRef = useRef(onStateChange);
  stateChangeRef.current = onStateChange;
  useEffect(() => {
    stateChangeRef.current?.({ dirty, busy, ready });
  }, [dirty, busy, ready]);
  const draftState = useRef({ dirty, busy });
  draftState.current = { dirty, busy };
  const incomingBinding = JSON.stringify(binding);
  useEffect(() => {
    const changedContext = initializedContext.current !== context;
    if (changedContext) {
      requestSequence.current += 1;
      running.current = false;
      setBusy(false);
      setMessage('');
      setFailed(false);
      saveError.current = '';
      setRemoteChanged(false);
      setAwaitingToken(false);
      setReloading(false);
      setConfirmReload(false);
      sourceBinding.current = {};
      pendingRemoteBinding.current = null;
      observedBinding.current = '';
      setPresentation(emptyProductPresentation());
      setDecorations([]);
      setGallery([]);
      setBaseline('');
      initializedContext.current = context;
    }
    if (loading || loadError || incomingBinding === observedBinding.current) return;
    observedBinding.current = incomingBinding;
    const nextBinding = JSON.parse(incomingBinding) as JsonRecord;
    const savedVersion = Date.parse(String(sourceBinding.current.updatedAt ?? ''));
    const incomingVersion = Date.parse(String(nextBinding.updatedAt ?? ''));
    if (!changedContext && incomingVersion < savedVersion) return;
    if (!changedContext && (draftState.current.dirty || draftState.current.busy)) {
      if (JSON.stringify(sourceBinding.current) !== incomingBinding) {
        pendingRemoteBinding.current = nextBinding;
        setRemoteChanged(true);
      }
      return;
    }
    const next = readDraft(nextBinding);
    sourceBinding.current = nextBinding;
    pendingRemoteBinding.current = null;
    setPresentation(next.presentation);
    setDecorations(next.decorations);
    setGallery(next.gallery);
    setBaseline(JSON.stringify(next));
    setRemoteChanged(false);
    setAwaitingToken(false);
  }, [context, incomingBinding, loading, loadError]);
  useEffect(() => {
    onDirtyChange(dirty || busy);
    return () => onDirtyChange(false);
  }, [dirty, busy, onDirtyChange]);
  const stops = Array.isArray(product.routeStops) ? product.routeStops.map(record) : [];
  const media = useMemo(() => {
    const values = Array.isArray(product.media)
      ? product.media
          .map(record)
          .filter(
            (m) =>
              m.status === 'ACTIVE' &&
              m.mediaType === 'IMAGE' &&
              (m.appId == null || m.appId === appId) &&
              isProductImageUrl(m.url),
          )
          .sort((left, right) => {
            const order = (value: JsonRecord) => {
              const raw = Number(value.sortOrder ?? value.sequence ?? 0);
              return Number.isFinite(raw) ? raw : 0;
            };
            return (
              order(left) - order(right) ||
              String(left.id ?? '').localeCompare(String(right.id ?? ''))
            );
          })
      : [];
    return values.map((m, i) => ({
      url: String(m.url),
      name: typeof m.altText === 'string' && m.altText.trim() ? m.altText : `图片 ${i + 1}`,
    }));
  }, [product.media, appId]);
  const foreignMedia = new Set(
    Array.isArray(product.media)
      ? product.media
          .map(record)
          .filter((m) => m.appId != null && m.appId !== appId)
          .map((m) => m.url)
      : [],
  );
  const thumbnail = (url: string, className = '') =>
    !foreignMedia.has(url) && (
      <ProductThumbnail
        api={api}
        productId={productId}
        scopeKey={appId}
        src={url}
        alt=""
        className={className}
      />
    );
  const imageOptions = [...media];
  for (const url of [
    ...gallery,
    ...decorations.flatMap((s) => (s.images ?? []).map((i) => i.url)),
    ...presentation.introduction.flatMap((b) => (b.type === 'image' ? [b.url] : [])),
  ]) {
    if (url && !foreignMedia.has(url) && !imageOptions.some((m) => m.url === url))
      imageOptions.push({ url, name: `已选图片 ${imageOptions.length + 1}` });
  }
  const defaultGalleryUrls = [...new Set(media.map((image) => image.url))];
  const coverUrl =
    typeof product.coverMediaUrl === 'string' &&
    isProductImageUrl(product.coverMediaUrl) &&
    defaultGalleryUrls.includes(product.coverMediaUrl)
      ? product.coverMediaUrl
      : (defaultGalleryUrls[0] ?? '');
  const effectiveGallery = (values: string[]) => {
    const valid = values.filter(isProductImageUrl);
    const configured = valid.length
      ? defaultGalleryUrls.filter((url) => valid.includes(url))
      : defaultGalleryUrls;
    // The customer detail always includes its cover, even outside an explicit gallery.
    return [...new Set([...configured, ...(coverUrl ? [coverUrl] : [])])];
  };
  const selectedGalleryUrls = effectiveGallery(gallery);
  const gallerySelectionDisabled = busy || externalBusy || !ready;
  const canChangeGallery = () =>
    !gallerySelectionDisabled &&
    !running.current &&
    mounted.current &&
    contextRef.current === context;
  const gallerySelection: ProductGallerySelectionState = {
    selectedUrls: selectedGalleryUrls,
    coverUrl,
    isDefault: !gallery.some(isProductImageUrl),
    disabled: gallerySelectionDisabled,
    onToggle: (url) => {
      if (!canChangeGallery() || url === coverUrl || !defaultGalleryUrls.includes(url)) return;
      setGallery((current) => {
        const effective = effectiveGallery(current);
        if (!effective.includes(url)) return [...effective, url];
        // Empty means automatic/all in the customer app, never "show no pictures".
        return effective.length > 1 ? effective.filter((selected) => selected !== url) : current;
      });
    },
    onReset: () => {
      if (canChangeGallery()) setGallery([]);
    },
  };
  const set = <K extends keyof ProductPresentation>(key: K, value: ProductPresentation[K]) =>
    setPresentation((p) => ({ ...p, [key]: value }));
  const blankIntroductionId = `intro-first-${productId}`;
  const displayedIntroduction: ProductIntroBlock[] = presentation.introduction.length
    ? presentation.introduction
    : [{ id: blankIntroductionId, type: 'paragraph', text: '' }];
  const updateBlock = (id: string, patch: Partial<ProductIntroBlock>) => {
    if (presentation.introduction.length === 0 && id === blankIntroductionId) {
      if ('text' in patch && patch.text)
        set('introduction', [
          { id, type: 'paragraph', text: patch.text, ...patch } as ProductIntroBlock,
        ]);
      return;
    }
    set(
      'introduction',
      presentation.introduction.map((b) =>
        b.id === id ? ({ ...b, ...patch } as ProductIntroBlock) : b,
      ),
    );
  };
  function moveBlock(index: number, delta: number) {
    const blocks = [...presentation.introduction];
    [blocks[index], blocks[index + delta]] = [blocks[index + delta]!, blocks[index]!];
    set('introduction', blocks);
  }
  function addBlock(type: ProductIntroBlock['type'], index = presentation.introduction.length) {
    const id = `content-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const block: ProductIntroBlock =
      type === 'image' ? { id, type, url: '', caption: '' } : { id, type, text: '' };
    const blocks = [...presentation.introduction];
    blocks.splice(index, 0, block);
    set('introduction', blocks);
  }
  function updateStop(name: string, patch: Partial<StopDecoration>) {
    const current = decorations.find((s) => s.name === name) ?? { name, tags: [], images: [] };
    setDecorations([...decorations.filter((s) => s.name !== name), { ...current, ...patch }]);
  }
  function validatedPresentation(): ProductPresentation | null {
    saveError.current = '';
    if (!ready || running.current || initializedContext.current !== context) {
      saveError.current = loadError || '线路介绍尚未准备好保存，请稍后重试。';
      return null;
    }
    setMessage('');
    setFailed(false);
    if (sourceBinding.current.id && typeof sourceBinding.current.updatedAt !== 'string') {
      setFailed(true);
      saveError.current = '暂未取得详情的保存版本，请重新加载后再保存；当前输入已保留。';
      setMessage(saveError.current);
      return null;
    }
    let valid: ProductPresentation;
    try {
      valid = parseProductPresentation(presentation);
    } catch (error) {
      saveError.current = error instanceof Error ? error.message : '请检查填写内容';
      setMessage(saveError.current);
      setFailed(true);
      return null;
    }
    return valid;
  }
  function validate(): boolean {
    return validatedPresentation() !== null;
  }
  function validateForPublish(): boolean {
    const valid = validatedPresentation();
    if (!valid) return false;
    if (valid.introduction.length === 0) {
      saveError.current = '请填写线路介绍，供顾客在详情页查看。';
      setFailed(true);
      setMessage(saveError.current);
      return false;
    }
    return true;
  }
  async function save(): Promise<boolean> {
    const valid = validatedPresentation();
    if (!valid) return false;
    if (!dirty) return true;
    running.current = true;
    setBusy(true);
    const sequence = ++requestSequence.current;
    const isCurrent = () =>
      mounted.current && contextRef.current === context && requestSequence.current === sequence;
    const bindingAtSave = sourceBinding.current;
    const original = record(bindingAtSave.detailContentOverride);
    const savedDraft = { presentation: valid, decorations, gallery };
    const detailContentOverride = {
      ...original,
      presentation: valid,
      galleryImageUrls: gallery,
      itinerary: { ...record(original.itinerary), version: 1, stops: decorations },
    };
    try {
      const response = record(
        await api.send(
          `/api/admin/m2/apps/${encodeURIComponent(appId)}/products/${encodeURIComponent(String(product.id))}/presentation`,
          'PUT',
          {
            ...(bindingAtSave.id
              ? {
                  requireExisting: true,
                  ...(bindingAtSave.updatedAt
                    ? { expectedUpdatedAt: bindingAtSave.updatedAt }
                    : {}),
                }
              : { requireAbsent: true }),
            detailContentOverride,
          },
        ),
      );
      if (!isCurrent()) return false;
      saveError.current = '';
      const hasToken = Boolean(response.id) && typeof response.updatedAt === 'string';
      sourceBinding.current = hasToken
        ? { ...bindingAtSave, ...response, detailContentOverride }
        : bindingAtSave;
      setPresentation(valid);
      setBaseline(JSON.stringify(savedDraft));
      setRemoteChanged(
        pendingRemoteBinding.current !== null &&
          pendingRemoteBinding.current.updatedAt !== response.updatedAt,
      );
      setAwaitingToken(!hasToken);
      setMessage(hasToken ? '线路详情已保存' : '内容已保存，正在核对最新版本…');
      onDone();
      return hasToken;
    } catch (error) {
      if (!isCurrent()) return false;
      setFailed(true);
      saveError.current =
        error instanceof ApiError && error.status === 409
          ? `${error.message}。你的未保存内容已保留，可检查当前输入，或重新载入已保存详情后再编辑。`
          : error instanceof ApiError
            ? `${error.message}；未保存内容已保留，可重试。`
            : '保存失败，未保存内容已保留，请重试。';
      setMessage(saveError.current);
      return false;
    } finally {
      if (isCurrent()) {
        running.current = false;
        setBusy(false);
      }
    }
  }
  useImperativeHandle(ref, () => ({
    validate,
    validateForPublish,
    save,
    getSaveError: () => saveError.current,
  }));
  async function reloadSaved() {
    if (running.current) return;
    running.current = true;
    setConfirmReload(false);
    setBusy(true);
    setReloading(true);
    setMessage('');
    setFailed(false);
    saveError.current = '';
    const sequence = ++requestSequence.current;
    const isCurrent = () =>
      mounted.current && contextRef.current === context && requestSequence.current === sequence;
    try {
      const response = await api.get<JsonRecord>(
        `/api/admin/m2/apps/${encodeURIComponent(appId)}/products/${encodeURIComponent(productId)}/override`,
      );
      if (!isCurrent()) return;
      if (!Object.prototype.hasOwnProperty.call(response, 'appOverride'))
        throw new Error('服务器未返回有效的详情记录');
      const latestBinding = record(response.appOverride);
      if (
        response.appOverride !== null &&
        (!latestBinding.id || typeof latestBinding.updatedAt !== 'string')
      )
        throw new Error('服务器未返回有效的详情版本');
      const next = readDraft(latestBinding);
      sourceBinding.current = latestBinding;
      observedBinding.current = incomingBinding;
      pendingRemoteBinding.current = null;
      setPresentation(next.presentation);
      setDecorations(next.decorations);
      setGallery(next.gallery);
      setBaseline(JSON.stringify(next));
      setRemoteChanged(false);
      setAwaitingToken(false);
      setMessage('已重新载入服务器上的最新详情，可以继续编辑');
      onDone();
    } catch (error) {
      if (!isCurrent()) return;
      setFailed(true);
      setMessage(
        `${error instanceof ApiError ? error.message : '重新读取失败'}；当前输入已保留，请重试。`,
      );
    } finally {
      if (isCurrent()) {
        running.current = false;
        setBusy(false);
        setReloading(false);
      }
    }
  }
  const moreInfoCount = [
    presentation.departureCity,
    presentation.accommodation,
    presentation.groupLabel,
    presentation.highlights.filter(Boolean).join(''),
  ].filter((value) => value.trim()).length;
  const status = busy
    ? reloading
      ? '正在重新读取已保存详情…'
      : '正在保存…'
    : dirty && !failed
      ? '有未保存的修改'
      : message || '已与保存内容一致';
  const saveButton = (
    <button
      type="button"
      className="primary"
      disabled={!dirty || busy || externalBusy || !ready}
      onClick={() => void save()}
    >
      {busy ? (reloading ? '读取中…' : '保存中…') : '保存线路详情'}
    </button>
  );
  const ToolsContainer = integrated ? 'div' : 'details';
  const introductionFields = (
    <>
      <div className="product-presentation-editor__blocks">
        {displayedIntroduction.map((block, index) => (
          <article key={block.id}>
            {presentation.introduction.length > 0 && (
              <div className="product-presentation-editor__block-head">
                <b>
                  {index + 1} ·{' '}
                  {block.type === 'heading' ? '标题' : block.type === 'paragraph' ? '正文' : '图片'}
                </b>
                <ToolsContainer
                  className={`product-presentation-editor__block-tools${integrated ? ' product-presentation-editor__block-tools--expanded' : ''}`}
                >
                  {!integrated && <summary>调整这段内容</summary>}
                  <div>
                    <button
                      type="button"
                      aria-label={`上移第${index + 1}块`}
                      disabled={index === 0}
                      onClick={() => moveBlock(index, -1)}
                    >
                      上移
                    </button>
                    <button
                      type="button"
                      aria-label={`下移第${index + 1}块`}
                      disabled={index === presentation.introduction.length - 1}
                      onClick={() => moveBlock(index, 1)}
                    >
                      下移
                    </button>
                    <select
                      aria-label={`在第${index + 1}块后插入`}
                      value=""
                      onChange={(e) =>
                        addBlock(e.target.value as ProductIntroBlock['type'], index + 1)
                      }
                    >
                      <option value="">插入内容</option>
                      <option value="heading">标题</option>
                      <option value="paragraph">正文</option>
                      <option value="image">图片</option>
                    </select>
                    <button
                      type="button"
                      onClick={() =>
                        set(
                          'introduction',
                          presentation.introduction.filter((b) => b.id !== block.id),
                        )
                      }
                    >
                      移除
                    </button>
                  </div>
                </ToolsContainer>
              </div>
            )}
            {block.type === 'image' ? (
              <div className="product-presentation-editor__image-fields">
                {block.url && thumbnail(block.url, 'product-presentation-editor__image-preview')}
                <label>
                  图片
                  <select
                    value={block.url}
                    onChange={(e) => updateBlock(block.id, { url: e.target.value })}
                  >
                    <option value="">选择已上传图片</option>
                    {imageOptions.map((m) => (
                      <option key={m.url} value={m.url}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  图片说明
                  <input
                    value={block.caption}
                    maxLength={120}
                    onChange={(e) => updateBlock(block.id, { caption: e.target.value })}
                  />
                </label>
              </div>
            ) : block.type === 'heading' && !block.richText ? (
              <input
                aria-label={`第${index + 1}块标题`}
                placeholder="例如：线路介绍、行程安排、费用说明"
                maxLength={100}
                value={block.text}
                onChange={(e) => updateBlock(block.id, { text: e.target.value })}
              />
            ) : (
              <Suspense fallback={<div role="status">正在打开图文编辑器…</div>}>
                <ProductRichEditor
                  key={`${appId}:${productId}:${block.id}`}
                  label={`第${index + 1}块正文`}
                  text={block.text}
                  value={block.richText}
                  disabled={busy || externalBusy || !ready}
                  images={imageOptions}
                  renderThumbnail={thumbnail}
                  onChange={(value) => updateBlock(block.id, value)}
                />
              </Suspense>
            )}
          </article>
        ))}
      </div>
      <div className="product-presentation-editor__add">
        <button type="button" onClick={() => addBlock('paragraph')}>
          {integrated ? '添加文字' : '添加一段文字'}
        </button>
        <button type="button" onClick={() => addBlock('image')}>
          {integrated ? '插入图片' : '添加图片'}
        </button>
        {integrated ? (
          <button type="button" onClick={() => addBlock('heading')}>
            添加标题
          </button>
        ) : (
          <ToolsContainer
            className={`product-presentation-editor__block-tools${integrated ? ' product-presentation-editor__block-tools--expanded' : ''}`}
          >
            {integrated ? <span>更多排版</span> : <summary>更多排版</summary>}
            <div>
              <button type="button" onClick={() => addBlock('heading')}>
                添加小标题
              </button>
            </div>
          </ToolsContainer>
        )}
      </div>
    </>
  );
  const durationFields = (
    <>
      <div className="product-presentation-editor__subheading product-presentation-editor__subheading--first">
        <h3>行程时长（可后补）</h3>
      </div>
      <div className="product-presentation-editor__fields">
        <label>
          行程天数
          <input
            type="number"
            min="1"
            max="365"
            value={presentation.days ?? ''}
            onChange={(e) => set('days', e.target.value === '' ? null : Number(e.target.value))}
            placeholder="未填写时按套餐时长显示"
          />
        </label>
        <label>
          住宿晚数
          <input
            type="number"
            min="0"
            max="364"
            value={presentation.nights ?? ''}
            onChange={(e) => set('nights', e.target.value === '' ? null : Number(e.target.value))}
            placeholder="当天往返填 0"
          />
        </label>
      </div>
    </>
  );
  const extraInformationFields = (
    <>
      <label>
        出发城市
        <input
          value={presentation.departureCity}
          maxLength={40}
          onChange={(e) => set('departureCity', e.target.value)}
          placeholder="例如：景洪"
        />
      </label>
      <label>
        住宿标准
        <input
          value={presentation.accommodation}
          maxLength={60}
          onChange={(e) => set('accommodation', e.target.value)}
          placeholder="例如：不含住宿 / 五星酒店"
        />
      </label>
      <label>
        成团方式
        <input
          value={presentation.groupLabel}
          onChange={(e) => set('groupLabel', e.target.value)}
          list={`product-group-${appId}-${productId}`}
          maxLength={30}
          placeholder="可选择或输入，例如：独立包车"
        />
        <datalist id={`product-group-${appId}-${productId}`}>
          {['自由行', '独立成团', '散客拼团', '私享小团'].map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
      </label>
    </>
  );
  const highlightFields = (
    <>
      <label className="product-presentation-editor__wide">
        <span
          className={
            integrated && !afterGallery ? 'product-presentation-editor__sr-only' : undefined
          }
        >
          线路亮点
        </span>
        <input
          value={presentation.highlights.join('，')}
          onChange={(e) => set('highlights', e.target.value.split(/[，,]/u))}
          placeholder="逗号分隔，例如：纯玩无购物，中文向导，含往返接送"
        />
      </label>
    </>
  );
  const galleryFields = (
    <>
      <div className="product-presentation-editor__subheading">
        <h3>头图轮播</h3>
        <small>从已上传产品图片中选择；未选择时使用产品图库</small>
      </div>
      <div className="product-presentation-editor__choices">
        {imageOptions.map((m) => (
          <label key={m.url}>
            <input
              type="checkbox"
              checked={gallery.includes(m.url)}
              onChange={(e) =>
                setGallery(
                  e.target.checked ? [...gallery, m.url] : gallery.filter((url) => url !== m.url),
                )
              }
            />
            {thumbnail(m.url)}
            {m.name}
          </label>
        ))}
      </div>
      {imageOptions.length === 0 && (
        <p className="product-presentation-editor__hint">
          尚未上传产品图片。可先填写文字，再在“产品图片”中上传，上传后即可在这里选择。
        </p>
      )}
    </>
  );
  const itineraryFields = (
    <>
      <div className="product-presentation-editor__subheading">
        <h3>行程节点图文</h3>
        <a
          href={`/admin/products/${encodeURIComponent(String(product.id))}/itinerary?appId=${encodeURIComponent(appId)}`}
        >
          维护节点名称、说明与顺序
        </a>
      </div>
      <div className="product-presentation-editor__stops">
        {stops.map((s, index) => {
          const name = String(s.name),
            item = decorations.find((d) => d.name === name) ?? { tags: [], images: [] };
          return (
            <div key={String(s.id ?? name)} className="product-presentation-editor__stop">
              <b>
                {String(index + 1).padStart(2, '0')} · {name}
              </b>
              <label>
                节点标签
                <input
                  aria-label={`${name}标签`}
                  value={(item.tags ?? []).join('，')}
                  onChange={(e) => updateStop(name, { tags: e.target.value.split(/[，,]/u) })}
                  placeholder="最多 4 个，逗号分隔"
                />
              </label>
              <div className="product-presentation-editor__choices" aria-label={`${name}配图`}>
                {imageOptions.map((m) => (
                  <label key={m.url}>
                    <input
                      type="checkbox"
                      checked={(item.images ?? []).some((i) => i.url === m.url)}
                      onChange={(e) =>
                        updateStop(name, {
                          images: e.target.checked
                            ? [...(item.images ?? []), { url: m.url, caption: name }]
                            : item.images.filter((i) => i.url !== m.url),
                        })
                      }
                    />
                    {thumbnail(m.url)}
                    {m.name}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {stops.length === 0 && (
        <p className="product-presentation-editor__hint">
          暂无单独的行程节点，也可以直接在“线路介绍”中填写完整行程。
        </p>
      )}
    </>
  );
  return (
    <section
      className={`product-presentation-editor${integrated ? ' product-presentation-editor--integrated' : ''}`}
      aria-label="线路介绍与详情展示"
    >
      {(!integrated || failed || Boolean(loadError) || remoteChanged || awaitingToken) && (
        <div className="product-presentation-editor__title">
          <div>
            <h2>{integrated ? '详情保存提示' : '线路介绍'}</h2>
            {!integrated && <p>直接填写游客要看的介绍，也可以添加图片。</p>}
          </div>
          {(!integrated || failed || Boolean(loadError) || remoteChanged || awaitingToken) && (
            <div className="product-presentation-editor__title-actions">
              <button
                type="button"
                aria-label="重新载入已保存详情（放弃当前编辑）"
                disabled={busy || loading || externalBusy}
                onClick={() => setConfirmReload(true)}
              >
                重新载入已保存详情
              </button>
              {!integrated && saveButton}
            </div>
          )}
        </div>
      )}
      {(!integrated || loading || Boolean(loadError) || failed || awaitingToken) && (
        <div className="product-presentation-editor__status" aria-live="polite">
          <span role="status" className={failed ? 'product-presentation-editor__error' : ''}>
            {loading ? '正在读取已保存的详情…' : loadError || status}
          </span>
          {!sourceBinding.current.id && !loading && !loadError && (
            <span>首次保存将建立详情草稿</span>
          )}
        </div>
      )}
      {remoteChanged && (
        <p className="product-presentation-editor__warning" role="alert">
          服务器上的详情已更新。你的未保存内容已保留，保存时会检查版本，避免覆盖他人的修改。
        </p>
      )}
      {loadError && (
        <p className="product-presentation-editor__error" role="alert">
          暂时无法读取已保存内容，请重新载入已保存详情后再保存；当前输入会保留。
        </p>
      )}
      {integrated ? (
        <div className="product-presentation-editor__flow">
          {renderGallery ? (
            renderGallery(gallerySelection)
          ) : (
            <>
              {galleryStart}
              <fieldset
                disabled={busy || externalBusy || !ready}
                className="product-presentation-editor__flow-section"
                data-section="gallery-selection"
                aria-label="小程序头图轮播"
              >
                {galleryFields}
              </fieldset>
            </>
          )}
          <div className="product-presentation-editor__identity" data-section="route-identity">
            {afterGallery}
            <fieldset
              disabled={busy || externalBusy || !ready}
              className="product-presentation-editor__flow-section"
              data-section="route-highlights"
              aria-label="小程序线路亮点"
            >
              {!afterGallery && (
                <div className="product-presentation-editor__subheading">
                  <h3>线路亮点</h3>
                </div>
              )}
              <small className="product-presentation-editor__highlight-hint">
                显示在线路名称下方
              </small>
              {highlightFields}
            </fieldset>
          </div>
          <fieldset
            disabled={busy || externalBusy || !ready}
            className="product-presentation-editor__flow-section"
            data-section="route-introduction"
            aria-label="小程序图文介绍"
          >
            <div className="product-presentation-editor__subheading">
              <h3>线路介绍</h3>
              <small>按下方顺序展示文字和图片</small>
            </div>
            {introductionFields}
          </fieldset>
          {beforeSupplementary}
        </div>
      ) : (
        <fieldset disabled={busy || externalBusy || !ready}>
          {introductionFields}
          {durationFields}
          <details
            className="product-presentation-editor__secondary"
            data-section="more-route-info"
          >
            <summary>
              更多线路信息<span>{moreInfoCount ? `已填 ${moreInfoCount} 项` : '按需填写'}</span>
            </summary>
            <div className="product-presentation-editor__fields">
              {extraInformationFields}
              {highlightFields}
            </div>
          </details>
          <details
            className="product-presentation-editor__secondary"
            data-section="advanced-images"
          >
            <summary>轮播与行程配图（按需设置）</summary>
            <p className="product-presentation-editor__hint">
              未设置头图轮播时自动使用产品图库；行程节点配图可以按需补充。
            </p>
            {galleryFields}
            {itineraryFields}
          </details>
          <div className="product-presentation-editor__footer">
            <span className={failed ? 'product-presentation-editor__error' : ''}>{status}</span>
            {saveButton}
          </div>
        </fieldset>
      )}
      <ConfirmDialog
        open={confirmReload}
        title="重新载入已保存详情？"
        description="将重新读取服务器上的最新详情。读取成功后，本次尚未保存的编辑会被替换；读取失败会保留当前输入。"
        confirmLabel="放弃当前编辑并重新载入"
        onCancel={() => setConfirmReload(false)}
        onConfirm={() => void reloadSaved()}
      />
    </section>
  );
});
